import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AuthErrorCode } from 'helpers';

interface AuthErrorLink {
  labelKey: string;
  to: string;
}

const ERROR_LINKS: Partial<Record<AuthErrorCode, AuthErrorLink>> = {
  invalidCredentials: {
    labelKey: 'authAlert.actions.resetPassword',
    to: '/forgot-password',
  },
  emailAlreadyExists: {
    labelKey: 'authAlert.actions.goToSignIn',
    to: '/login',
  },
};

const ERRORS_WITH_EMAIL: AuthErrorCode[] = ['emailAlreadyExists'];

const ERRORS_WITH_NOTE: AuthErrorCode[] = [
  'verificationEmailFailed',
  'server',
  'unknown',
];

const TONE = {
  container: 'border-[#F1C9C6] bg-[#FDF2F1]',
  badge: 'bg-[#CC3F3F]',
  note: 'text-[#8A5B58]',
  link: 'text-[#CC3F3F]',
};

interface AuthErrorMessageProps {
  code: AuthErrorCode;
  email?: string;
}

export const AuthErrorMessage: React.FC<AuthErrorMessageProps> = ({
  code,
  email,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const link = ERROR_LINKS[code];
  const showEmail = Boolean(email) && ERRORS_WITH_EMAIL.includes(code);
  const showNote = ERRORS_WITH_NOTE.includes(code);

  return (
    <div
      role="alert"
      className={`w-full flex items-start gap-[12px] rounded-[14px] border px-[14px] py-[12px] ${TONE.container}`}
    >
      <span
        aria-hidden="true"
        className={`mt-[2px] flex h-[20px] w-[20px] flex-none items-center justify-center rounded-full text-[13px] font-[700] leading-none text-white ${TONE.badge}`}
      >
        !
      </span>
      <div className="flex-1 text-left space-y-[6px]">
        <p className="text-[14px] font-[600] leading-[1.35] text-[#2E435F]">
          {t(`authAlert.errors.${code}.title`)}
        </p>
        <p
          className="text-[13px] leading-[1.45] text-[#58697F]"
          dangerouslySetInnerHTML={{
            __html: String(t(`authAlert.errors.${code}.text`)),
          }}
        />
        {showEmail && (
          <p className="text-[13px] font-[600] leading-[1.45] break-all text-[#2E435F]">
            {email}
          </p>
        )}
        {showNote && (
          <p
            className={`text-[12px] leading-[1.45] ${TONE.note}`}
            dangerouslySetInnerHTML={{
              __html: String(t(`authAlert.errors.${code}.note`)),
            }}
          />
        )}
        {link && (
          <button
            type="button"
            onClick={() => navigate(link.to)}
            className={`text-[13px] font-[600] underline underline-offset-2 hover:no-underline ${TONE.link}`}
          >
            {t(link.labelKey)}
          </button>
        )}
      </div>
    </div>
  );
};
